import {
  FilePenRegularIcon,
  PersonWalkingDashedLineArrowRightRegularIcon,
  UserDoctorRegularIcon,
} from "@elixir/icons";
import { usePackageBookingStore } from "@elixir/stores";
import {
  Appointment,
  MODES_OF_APT,
  PACKAGE_POPUP_TYPE,
  SUBJECT_KEYS,
} from "@elixir/types";
import { Card, Dropdown, Popover, Tooltip } from "antd";
import dateFormat from "dateformat";
import { MouseEventHandler, useMemo, useState } from "react";
import {
  DoctorChipUi,
  EkaPassTagChipUi,
  NewPatientChipUi,
  OverlayedTagChipContainerUi,
  PackageMiniChipUi,
  SecondaryButton,
  SecondaryIconButton,
  TeleStatusChipUi,
} from "ui";
import useCheckInAppointment from "../../../apis/tryst/check-in-appointment";
import useArchiveAppointment from "../../../apis/tryst/delete-archive-appointment";
import { ReactComponent as BellSolidIcon } from "../../../assets/icons/font-awesome/bell-solid.svg";
import { ReactComponent as CalendarPenSolidIcon } from "../../../assets/icons/font-awesome/calendar-pen-solid.svg";
import { ReactComponent as CalendarSolidIcon } from "../../../assets/icons/font-awesome/calendar-solid.svg";
import { ReactComponent as EllipsisRegularIcon } from "../../../assets/icons/font-awesome/ellipsis-regular.svg";
import { ReactComponent as TrashRegularIcon } from "../../../assets/icons/font-awesome/trash-regular.svg";
import { ReactComponent as HouseChimenyRegularRegularIcon } from "../../../assets/icons/font-awesome/house-chimney-medical-regular.svg";
import IconButton from "../../../atoms/button/icon-button";
import { QUEUE_TYPE } from "../../../constants/appointments";
import { MainMenu } from "../../../organisms/queue/more-menu";
import useQueueStore from "../../../stores/queue-store";
import { getAppointmentCardProps } from "../../../utils/get-appointment-card-prop";
import PastVisit from "../../button/queue/columns/past-visit";
import ShowSelectedTags from "../../button/queue/columns/show-selected-tags";
import DeleteAppointmentPopover from "./delete-appointment-popover";
import SelectSlot from "./select-slot";
import SendReminder from "./send-reminder";
import UserData from "./user-data";
import {
  getCreateApptConfig,
  onAppointmentAction,
} from "../../../utils/bridges/methods";
import useGetQueueConfig from "../../../apis/tryst/get-queue-config";
import { useMyAccessProfileV2 } from "../../../apis/tryst/get-my-access-profile";
import { getAclViewClassName, isClinicLevelApt } from "@elixir/utils";
import SelectDoctorMenu from "./select-doctor-menu";
import AddServicesToAppointment from "../../button/queue/columns/add-services-to-appointment";
import { useQuery } from "@tanstack/react-query";
import { CREATE_APPT_CONFIG_QUERY_KEY } from "../../../constants/query-keys";

export const BottomButtonRow = ({
  aptData,
  canOpenPopup,
  queueType,
}: {
  aptData: Appointment;
  canOpenPopup?: boolean;
  queueType: QUEUE_TYPE;
}) => {
  const { mutateAsync: putCheckInAppointment, isLoading } =
    useCheckInAppointment();
  const { data: queueConfigData } = useGetQueueConfig();
  const { apt_config } = queueConfigData?.data || {};
  const { pre_consulation_notes } = apt_config || {};

  const setRightPanelQueueType = useQueueStore(
    (state) => state.setRightPanelQueueType
  );
  const setSelectedDate = useQueueStore((state) => state.setSelectedDate);
  const setCustomSlotDate = useQueueStore((state) => state.setCustomSlotDate);
  const setOpenLeftPanel = useQueueStore((state) => state.setOpenLeftPanel);
  const setScrollToValues = useQueueStore((state) => state.setScrollToValues);
  const setShowRequestedAptPopup = useQueueStore(
    (state) => state.setShowRequestedAptPopup
  );

  const onCheckIn = async () => {
    const response = await putCheckInAppointment({ appointment: aptData });
    if (response.success) {
      setRightPanelQueueType(QUEUE_TYPE.CHECKED_IN);
      setSelectedDate(new Date());
      if (document.body.clientWidth < 1280) {
        setOpenLeftPanel(false);
      }
      setScrollToValues({ aid: aptData.aid, queueType: QUEUE_TYPE.CHECKED_IN });
    }
  };

  const handleCheckinClick: MouseEventHandler<HTMLElement> = async (e) => {
    e.stopPropagation();
    await onCheckIn();
  };

  const [sendReminderOpen, setSendReminderOpen] = useState(false);
  const isFollowup = queueType === "followup";

  return (
    <div className="dr-flex dr-items-center dr-space-x-16 dr-h-32">
      {queueType === QUEUE_TYPE.BOOKED &&
        isClinicLevelApt({ docId: aptData.doctor_oid }) ? (
        <Dropdown
          className=""
          trigger={["click"]}
          overlay={<SelectDoctorMenu aptData={aptData} />}
        >
          <Tooltip destroyTooltipOnHide title={"Assign Doctor"}>
            <IconButton
              activeClass="hover:dr-border-darwin-touch-neutral-100 focus:dr-border-darwin-touch-neutral-100 active:dr-border-darwin-touch-neutral-100 hover:dr-bg-darwin-touch-neutral-100 active:dr-bg-darwin-touch-neutral-100 dr-text-darwin-touch-neutral-600 hover:dr-text-darwin-touch-neutral-800 active:dr-text-darwin-touch-neutral-800 focus:dr-text-darwin-touch-neutral-800 dr-border-darwin-touch-neutral-100 dr-bg-darwin-touch-neutral-0 focus:dr-bg-darwin-touch-neutral-50"
              disabledClass="dr-border-darwin-touch-neutral-300 dr-text-darwin-touch-neutral-300 dr-bg-darwin-touch-neutral-0"
              className="dr-w-32 dr-min-h-32 dr-h-32"
              icon={<UserDoctorRegularIcon />}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Dropdown>
      ) : !pre_consulation_notes &&
        ["booked", "followup"].includes(queueType) ? (
        <Popover
          style={{ borderRadius: "1rem" }}
          placement="left"
          trigger={["click"]}
          destroyTooltipOnHide
          open={sendReminderOpen}
          onOpenChange={(value) => setSendReminderOpen(value)}
          content={
            <SendReminder
              queueType={queueType}
              onClose={() => setSendReminderOpen(false)}
              data={aptData}
              className="dr-max-w-375"
            />
          }
          overlayClassName="popover-p-0"
        >
          <Tooltip
            destroyTooltipOnHide
            title={isFollowup ? "Send Follow-up Reminder" : "Send Reminder"}
          >
            <IconButton
              activeClass="hover:dr-border-darwin-touch-neutral-100 focus:dr-border-darwin-touch-neutral-100 active:dr-border-darwin-touch-neutral-100 hover:dr-bg-darwin-touch-neutral-100 active:dr-bg-darwin-touch-neutral-100 dr-text-darwin-touch-neutral-600 hover:dr-text-darwin-touch-neutral-800 active:dr-text-darwin-touch-neutral-800 focus:dr-text-darwin-touch-neutral-800 dr-border-darwin-touch-neutral-100 dr-bg-darwin-touch-neutral-0 focus:dr-bg-darwin-touch-neutral-50"
              disabledClass="dr-border-darwin-touch-neutral-300 dr-text-darwin-touch-neutral-300 dr-bg-darwin-touch-neutral-0"
              className={`dr-w-32 dr-min-h-32 dr-h-32 ${getAclViewClassName(SUBJECT_KEYS.SEND_REMINDER)}`}
              icon={<BellSolidIcon />}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Popover>
      ) : null}
      {!pre_consulation_notes && ["booked"].includes(queueType) ? (
        <Popover
          style={{ borderRadius: "16px" }}
          placement="rightTop"
          trigger={["click"]}
          content={
            <SelectSlot
              onSubmitClick={() => {
                // setBookedSelectSlot(false);
              }}
              aptData={aptData}
              bookType={"RESCHEDULE"}
              aptChannel={"BOOKED"}
            />
          }
          overlayClassName="popover-p-0"
        >
          <Tooltip destroyTooltipOnHide title="Reschedule Appointment">
            <IconButton
              activeClass="hover:dr-border-darwin-touch-neutral-100 focus:dr-border-darwin-touch-neutral-100 active:dr-border-darwin-touch-neutral-100 hover:dr-bg-darwin-touch-neutral-100 active:dr-bg-darwin-touch-neutral-100 dr-text-darwin-touch-neutral-600 hover:dr-text-darwin-touch-neutral-800 active:dr-text-darwin-touch-neutral-800 focus:dr-text-darwin-touch-neutral-800 dr-border-darwin-touch-neutral-100 dr-bg-darwin-touch-neutral-0 focus:dr-bg-darwin-touch-neutral-50"
              disabledClass="dr-border-darwin-touch-neutral-300 dr-text-darwin-touch-neutral-300 dr-bg-darwin-touch-neutral-0"
              className="dr-w-32 dr-min-h-32 dr-h-32"
              icon={<CalendarPenSolidIcon />}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Popover>
      ) : null}
      {!pre_consulation_notes && ["followup"].includes(queueType) ? (
        <Popover
          style={{ borderRadius: "16px" }}
          placement="rightTop"
          trigger={["click"]}
          content={
            <SelectSlot
              onSubmitClick={() => {
                // setFollowupBookSlot(false);
              }}
              aptData={aptData}
              bookType={"SCHEDULE"}
              aptChannel={"FOLLOW-UP"}
            />
          }
          overlayClassName="popover-p-0"
        >
          <Tooltip destroyTooltipOnHide title="Book Appointment">
            <IconButton
              activeClass="hover:dr-border-darwin-touch-neutral-100 focus:dr-border-darwin-touch-neutral-100 active:dr-border-darwin-touch-neutral-100 hover:dr-bg-darwin-touch-neutral-100 active:dr-bg-darwin-touch-neutral-100 dr-text-darwin-touch-neutral-600 hover:dr-text-darwin-touch-neutral-800 active:dr-text-darwin-touch-neutral-800 focus:dr-text-darwin-touch-neutral-800 dr-border-darwin-touch-neutral-100 dr-bg-darwin-touch-neutral-0 focus:dr-bg-darwin-touch-neutral-50"
              disabledClass="dr-border-darwin-touch-neutral-300 dr-text-darwin-touch-neutral-300 dr-bg-darwin-touch-neutral-0"
              className="dr-w-32 dr-min-h-32 dr-h-32"
              icon={<CalendarSolidIcon />}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Popover>
      ) : null}
      {["booked", "followup"].includes(queueType) && canOpenPopup ? (
        <Dropdown
          className=""
          trigger={["click"]}
          overlay={<MainMenu apt={aptData} queueType={queueType} />}
        >
          <IconButton
            activeClass="hover:dr-border-darwin-touch-neutral-100 focus:dr-border-darwin-touch-neutral-100 active:dr-border-darwin-touch-neutral-100 hover:dr-bg-darwin-touch-neutral-100 active:dr-bg-darwin-touch-neutral-100 dr-text-darwin-touch-neutral-600 hover:dr-text-darwin-touch-neutral-800 active:dr-text-darwin-touch-neutral-800 focus:dr-text-darwin-touch-neutral-800 dr-border-darwin-touch-neutral-100 dr-bg-darwin-touch-neutral-0 focus:dr-bg-darwin-touch-neutral-50"
            disabledClass="dr-border-darwin-touch-neutral-300 dr-text-darwin-touch-neutral-300 dr-bg-darwin-touch-neutral-0"
            className="dr-w-32 dr-min-h-32 dr-h-32"
            icon={<EllipsisRegularIcon />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ) : null}
      {pre_consulation_notes && ["booked", "followup"].includes(queueType) ? (
        <SecondaryButton
          title="Notes"
          onClick={() =>
            onAppointmentAction({
              a: aptData,
              cId: aptData.clinic_id,
              dontAllowStartVisit: true,
              isNotesMode: true,
            })
          }
          IconComponent={() => (
            <FilePenRegularIcon className="dr-h-16 dr-w-16" />
          )}
          buttonIconPosition="left"
          extraClass={`dr-flex-1 dr-w-full ${getAclViewClassName(SUBJECT_KEYS.UPDATE_NOTES)}`}
        />
      ) : null}
      {queueType === QUEUE_TYPE.BOOKED &&
        isClinicLevelApt({ docId: aptData.doctor_oid }) ? (
        <Dropdown
          className=""
          trigger={["click"]}
          overlay={<SelectDoctorMenu aptData={aptData} onSelect={onCheckIn} />}
        >
          <SecondaryButton
            title="Check in"
            onClick={() => { }}
            IconComponent={() => (
              <PersonWalkingDashedLineArrowRightRegularIcon />
            )}
            buttonIconPosition="left"
            isLoading={isLoading}
            isDisabled={isLoading}
            extraClass={`dr-flex-1 dr-w-full ${getAclViewClassName(SUBJECT_KEYS.CHECKIN_APPT)}`}
          />
        </Dropdown>
      ) : ["booked", "followup"].includes(queueType) ? (
        <SecondaryButton
          title="Check in"
          onClick={handleCheckinClick}
          IconComponent={() => <PersonWalkingDashedLineArrowRightRegularIcon />}
          buttonIconPosition="left"
          isLoading={isLoading}
          isDisabled={isLoading}
          extraClass={`dr-flex-1 dr-w-full ${getAclViewClassName(SUBJECT_KEYS.CHECKIN_APPT)}`}
        />
      ) : null}
      {[QUEUE_TYPE.REQUESTED].includes(queueType) ? (
        <SecondaryIconButton
          borderColor="dr-border-darwin-touch-neutral-100 hover:dr-border-darwin-touch-neutral-100"
          IconComponent={() => (
            <PersonWalkingDashedLineArrowRightRegularIcon className="dr-text-darwin-touch-neutral-600" />
          )}
          buttonColor="hover:dr-bg-darwin-touch-neutral-100 dr-bg-dariwn-touch-neutral-0"
          onClick={handleCheckinClick}
          tooltipText="Check in"
          extraClass={`${getAclViewClassName(SUBJECT_KEYS.CHECKIN_APPT)}`}
        />
      ) : null}
      {[QUEUE_TYPE.REQUESTED].includes(queueType) ? (
        <Popover
          style={{ borderRadius: "16px" }}
          placement="rightTop"
          trigger={["click"]}
          content={
            <SelectSlot
              onSubmitClick={() => {
                setShowRequestedAptPopup(false);
                setScrollToValues({
                  aid: aptData.aid,
                  queueType: QUEUE_TYPE.BOOKED,
                });
              }}
              aptData={aptData}
              bookType={"SCHEDULE"}
              aptChannel={"REQUESTED"}
            />
          }
          overlayClassName="popover-p-0"
        >
          <SecondaryButton
            title="Book slot"
            onClick={() => { }}
            IconComponent={() => <CalendarSolidIcon />}
            buttonIconPosition="left"
            extraClass="dr-flex-1 dr-w-full"
            borderColor="dr-border-darwin-touch-neutral-100"
          />
        </Popover>
      ) : null}
    </div>
  );
};

const LeftPanelCard = ({
  hideDeleteButton = false,
  aptData,
  queueType,
  hideActionButtons = false,
  isHighlighted = false,
}: {
  hideDeleteButton?: boolean;
  aptData: Appointment;
  queueType: QUEUE_TYPE;
  hideActionButtons?: boolean;
  isHighlighted?: boolean;
}) => {
  const { statusPanelChips = [] } = getAppointmentCardProps(aptData, queueType);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { data: myAccessProfile } = useMyAccessProfileV2();

  const { clinicId } = useQueueStore.getState();

  const { discount_conf, patient } = aptData;

  const packageInclusionInApt = aptData.package_inclusions?.[0];

  const isTeleApt = useMemo(() => {
    const isTeleApt = aptData.mode === MODES_OF_APT.TELE;
    return isTeleApt;
  }, [aptData]);

  const {
    mutateAsync: postArchiveAppointment,
    isLoading: isArchiveAppointmentLoading,
  } = useArchiveAppointment();

  const setPackagePopupType = usePackageBookingStore(
    (state) => state.setPackagePopupType
  );
  const setSelectedAppointment = usePackageBookingStore(
    (state) => state.setSelectedAppointment
  );

  const handlePackageChipClick = () => {
    setPackagePopupType(PACKAGE_POPUP_TYPE.CURRENT_TREATMENT);
    setSelectedAppointment(aptData);
  };

  const { doctorOfApt, clinicOfApt } = useMemo(() => {
    const doctorOfApt = myAccessProfile?.doctors?.find(
      (item) => item.oid === aptData.doctor_oid
    );
    const clinicOfApt = myAccessProfile?.clinics?.find(
      (item) => item.id === aptData.clinic_id
    );

    return { doctorOfApt, clinicOfApt };
  }, [
    aptData.clinic_id,
    aptData.doctor_oid,
    myAccessProfile?.clinics,
    myAccessProfile?.doctors,
  ]);

  const clinicLevelApt = isClinicLevelApt({ docId: aptData.doctor_oid });

  const { data: createApptConfig } = useQuery({
    queryKey: [CREATE_APPT_CONFIG_QUERY_KEY],
    queryFn: getCreateApptConfig,
  });

  const isServicesTurnedOn = useMemo(() => {
    if (!createApptConfig) return false;
    const associatePaymentsConfig = createApptConfig.find(
      (c) => c.id === "service"
    );
    return !!associatePaymentsConfig?.isShown;
  }, [createApptConfig]);

  return (
    <div className="dr-flex dr-space-x-8  dr-w-full">
      {[QUEUE_TYPE.BOOKED].includes(queueType) ? (
        <div
          className={`dr-flex dr-flex-col dr-items-start BodyBody2Bold  ${discount_conf || isTeleApt ? "dr-py-37" : "dr-py-10"
            }`}
        >
          <div className="dr-text-darwin-touch-neutral-1000">
            {dateFormat(aptData.start_time, "hh:MM")}
          </div>
          <div className="dr-text-darwin-touch-neutral-400 BodyBody2Regular">
            {dateFormat(aptData.start_time, "TT")}
          </div>
        </div>
      ) : null}
      {[QUEUE_TYPE.REQUESTED].includes(queueType) ? (
        <div
          className={`dr-flex dr-flex-col dr-items-start BodyBody2Bold  ${discount_conf || isTeleApt ? "dr-py-37" : "dr-py-10"
            }`}
        >
          <div className="dr-text-darwin-touch-neutral-1000">
            {dateFormat(aptData.created_at, "hh:MM")}
          </div>
          <div className="dr-text-darwin-touch-neutral-400 BodyBody2Regular">
            {dateFormat(aptData.created_at, "TT")}
          </div>
        </div>
      ) : null}
      {/* <Popover
        // open={canOpenPopup}
        style={{ borderRadius: "16px" }}
        placement="rightTop"
        content={
          <BookedDetailsPopover aptData={aptData} queueType={queueType} />
        }
        trigger="click"
      > */}
      <div className="dr-w-full">
        {discount_conf ||
          isTeleApt ||
          packageInclusionInApt ||
          myAccessProfile?.doctors.length ||
          clinicLevelApt ? (
          <div className="dr-flex dr-justify-start dr-ml-20 dr-space-x-8">
            {clinicId === "all" ? (
              <OverlayedTagChipContainerUi containerClassName="bg-darwin-neutral-100 dr-flex dr-flex-row dr-items-center">
                <HouseChimenyRegularRegularIcon className="dr-min-w-16 dr-w-16 dr-h-16" />
                <div className="CalloutRegular dr-ml-4 dr-text-darwin-neutral-1000">
                  {
                    myAccessProfile?.clinics.find(
                      (t) => t.id === aptData.clinic_id
                    )?.name
                  }
                </div>
              </OverlayedTagChipContainerUi>
            ) : null}
            {!!myAccessProfile?.doctors?.length &&
              myAccessProfile.doctors.length > 1 &&
              !clinicLevelApt ? (
              <DoctorChipUi name={doctorOfApt?.name!} pic={doctorOfApt?.pic} />
            ) : null}
            {clinicLevelApt ? (
              <DoctorChipUi
                name={clinicOfApt?.name!}
                pic={"https://cdn.eka.care/vagus/cm3cwdw5000000tj725smdgjo.png"}
              />
            ) : null}
            {aptData.new_patient ? <NewPatientChipUi /> : null}
            {discount_conf ? (
              <EkaPassTagChipUi
                discount={discount_conf?.discount}
                discountType={
                  discount_conf?.discount_type === "P" ? "percent" : "flat"
                }
                patientName={patient.fln}
              />
            ) : null}
            {isTeleApt ? <TeleStatusChipUi /> : null}
            {packageInclusionInApt?.inclusion_id ? (
              <PackageMiniChipUi onClick={handlePackageChipClick} />
            ) : null}
          </div>
        ) : (
          <></>
        )}

        <Card
          bodyStyle={{ padding: "0.75rem" }}
          className={`dr-rounded-8 dr-border-1 flex-1 dr-border-solid dr-border-darwin-touch-neutral-100 ${isHighlighted ? "dr-shadow-elevation-100" : ""
            }`}
        >
          <div className={`${hideActionButtons ? "" : "dr-space-y-16"}`}>
            <div className="dr-space-y-12">
              <div className="dr-flex dr-justify-between dr-space-x-16">
                <UserData apt={aptData} />
                {!hideDeleteButton ? (
                  queueType === QUEUE_TYPE.REQUESTED ? (
                    <SecondaryIconButton
                      IconComponent={() => (
                        <TrashRegularIcon className="dr-text-darwin-touch-red-primary" />
                      )}
                      onClick={() =>
                        postArchiveAppointment({ appointment: aptData })
                      }
                      isLoading={isArchiveAppointmentLoading}
                      borderColor="dr-border-darwin-touch-neutral-100"
                      buttonColor="dr-bg-darwin-touch-neutral-0 hover:dr-bg-darwin-touch-red-bg-light focus:dr-bg-darwin-touch-red-bg-light hover:dr-border-darwin-touch-red-light active:dr-border-darwin-touch-red-light focus:dr-border-darwin-touch-red-light"
                      tooltipText="Delete Request"
                    />
                  ) : (
                    <Popover
                      style={{ borderRadius: "16px" }}
                      open={deleteOpen}
                      onOpenChange={(value) => setDeleteOpen(value)}
                      placement="rightTop"
                      trigger={["click"]}
                      destroyTooltipOnHide
                      content={
                        <DeleteAppointmentPopover
                          queueType={queueType}
                          onClose={() => {
                            setDeleteOpen(false);
                          }}
                          apt={aptData}
                        />
                      }
                      overlayClassName="popover-p-0"
                    >
                      <Tooltip destroyTooltipOnHide title="Delete Actions">
                        <IconButton
                          className="dr-w-32 dr-min-h-32 dr-h-32"
                          activeClass="dr-bg-darwin-touch-neutral-0 hover:dr-bg-darwin-touch-red-bg-light focus:dr-bg-darwin-touch-red-bg-light hover:dr-border-darwin-touch-red-light active:dr-border-darwin-touch-red-light focus:dr-border-darwin-touch-red-light dr-border-darwin-touch-neutral-100"
                          disabledClass=""
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          icon={
                            <TrashRegularIcon className="dr-text-darwin-touch-red-primary" />
                          }
                        />
                      </Tooltip>
                    </Popover>
                  )
                ) : null}
              </div>
              {statusPanelChips?.length > 0 ? (
                <div className="dr-flex dr-items-center dr-space-x-6">
                  {statusPanelChips?.map((status, index) => {
                    const { StyledIcon, text, backgroundColor, textColor } =
                      status || {};
                    return (
                      <div
                        key={index}
                        className={`dr-flex dr-items-center dr-rounded-4 BodyBody2Regular ${backgroundColor} ${textColor} dr-space-x-8 dr-px-8 dr-py-4 dr-w-full`}
                      >
                        <div className="dr-w-16 dr-h-16 dr-flex dr-items-center">
                          {StyledIcon}
                        </div>
                        <div>{text}</div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {["booked", "followup"].includes(queueType) ? (
                <div>
                  <div className="dr-flex dr-space-x-8 dr-items-center">
                    <ShowSelectedTags
                      apt={aptData}
                      queueType={queueType}
                      className="dr-flex-1 w-full"
                      numberOfChipsToShow={1}
                      popoverClassname="w-full"
                    />
                    {isServicesTurnedOn && (
                      <AddServicesToAppointment
                        apt={aptData}
                        numberOfChipsToShow={1}
                        className={"dr-max-w-330 w-full"}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
            {hideActionButtons ? null : (
              <BottomButtonRow
                aptData={aptData}
                queueType={queueType}
                canOpenPopup={true}
              />
            )}
          </div>
        </Card>
      </div>
      {/* </Popover> */}
      <style>{`
        .ant-popover-inner {
          border-radius: 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default LeftPanelCard;
